(ns nosco.richtext.markdown
  (:require
   ["marked" :refer [marked]]
   ["nosco-prosemirror/utils" :as nosco-pm-utils]
   ["prosemirror-model" :as pm-model]
   [cljs-bean.core :as bean]
   [clojure.string :as string]
   [nosco.richtext.prosemirror :as pm]
   [nosco.util :as util]))

(def markdown-block-types
  #{"doc"
    "paragraph"
    "heading"
    "ordered_list"
    "bullet_list"
    "list_item"})

(defn markdown-block? [node]
  (contains? markdown-block-types (.. node -type -name)))

(defn find-node-start [doc pos pred]
  (loop [pos pos]
    (let [$pos (.. doc (resolve pos))]
      (cond
        (<= pos 0)
        pos

        (pred (.. $pos (node)))
        (.. $pos (start))

        :else
        (recur (.. $pos (before)))))))

(defn find-node-end [doc pos pred]
  (loop [pos pos]
    (let [$pos (.. doc (resolve pos))]
      (cond
        (>= pos (.. doc -content -size))
        pos

        (pred (.. $pos (node)))
        (.. $pos (end))

        :else
        (recur (.. $pos (after)))))))

(defn expand-selection-for-markdown-tr
  "Expands the selection so that it doesn't split multiple blocks, e.g. the end of one
  paragraph and the beginning of another.

  In the case of a caret selection, the selection is expanded to the current block.

  In the case of a selection which starts in one block and ends in another, the
  start and end are expanded to their containing blocks.

  This ensures that we don't generate confusing Markdown, e.g. a partial list item, but
  still allows the user to select just a sentence or a single list item."
  [state]
  (let [{:keys [tr selection doc]} (bean/bean state)
        {:keys [from to]} (bean/bean selection)
        start-node (.. doc (resolve from) (node))
        end-node (.. doc (resolve to) (node))]
    (cond
      ;; Leave the selection as-is if the selection has a range within a single block.
      ;; Unless it's a caret selection, then expand to the whole containing block.
      (and (not= from to) ;; caret selection has (= from to)
           (identical? start-node end-node)
           (.-isBlock start-node))
      tr

      :else
      (let [block-start (find-node-start doc from markdown-block?)
            block-end (find-node-end doc to markdown-block?)
            selection (pm/->text-selection doc block-start block-end)]
        (pm/set-selection-tr state selection)))))

(defn markdown-bold [s]
  (str "**" s "**"))

(defn markdown-italic [s]
  (str "*" s "*"))

(defn markdown-link [text url title]
  (str "[" text "](" url (when title (str " \"" title "\"")) ")"))

(defn text-node-markdown [node]
  ;; A mark is (generally?) applied at most once to a node, so index the set by name for convenience
  ;; and then apply all of the marks we know about to the text
  (let [marks (into {} (map (juxt #(keyword (.. % -type -name)) identity)) (.. node -marks))]
    (cond-> (.-text node)
      (:link marks)
      (markdown-link (.. (:link marks) -attrs -href)
                     (.. (:link marks) -attrs -title))

      (:strong marks)
      (markdown-bold)

      (:em marks)
      (markdown-italic))))

(defn list-node? [node]
  (some? (#{"ordered_list" "bullet_list"} (.. node -type -name))))

(declare fragment-markdown)

(defn node-markdown [doc start-pos node]
  (case (.. node -type -name)
    "text"
    (text-node-markdown node)

    "heading"
    (str (string/join (repeat (.. node -attrs -level) "#"))
         " "
         (.-textContent node)
         "\n\n")

    "paragraph"
    ;; Recurse over the paragraph contents and append \n\n to non-empty paragraphs
    (let [markdown-content (fragment-markdown doc start-pos (.-content node))]
      (when-not (string/blank? markdown-content)
        (str markdown-content "\n\n")))

    "list_item"
    ;; We have to handle the list_item instead of getting to handle the ordered_list or bullet_list
    ;; node directly, becase the selection may only cover part of the list, in which case we
    ;; won't have the parent node in this fragment - the workaround is to use the position
    ;; to find the parent in the document.
    (let [$pos (.. doc (resolve (inc start-pos))) ;; HACK No idea why this needs to be incremented in some cases
          {list-node :node list-node-depth :depth} (bean/bean (nosco-pm-utils/findParentNodeClosestToPos $pos list-node?))
          item-index (.. $pos (index list-node-depth))
          last (= (.-childCount ^js list-node) (inc item-index))]
      (case (.. list-node -type -name)
        "ordered_list"
        (str (inc item-index) ". " (.-textContent node) "\n" (when last "\n"))

        "bullet_list"
        (str "- " (.-textContent node) "\n" (when last "\n"))))

    ;; For other node types, recursively process their children
    (fragment-markdown doc start-pos (.-content node))))

(defn fragment-markdown [doc start-pos fragment]
  (string/join
   (pm/map-fragment
    (fn [node offset]
      (node-markdown doc (+ start-pos offset) node))
    fragment)))

(defn selected-markdown
  "Returns the contents of the current selection as Markdown, or nil when no text is selected."
  [state]
  (let [{:keys [selection doc]} (bean/bean state)
        {:keys [from to]} (bean/bean selection)
        slice (.. doc (slice from to))]
    (util/non-blank (fragment-markdown doc from (.-content slice)))))

(defn insert-spacer-paragraphs [s]
  (string/replace s #"\n\n" "\n\n<p><br /></p>\n\n"))

(defn markdown->html [s]
  (-> s
      (insert-spacer-paragraphs)
      (marked #js {:gfm true})))

(defn markdown->slice
  "Parses a Markdown string into a Prosemirror Slice using the provided schema."
  [schema s]
  (let [parser (.. pm-model/DOMParser (fromSchema schema))
        el (.. js/document (createElement "div"))]
    (set! (.-innerHTML el) (markdown->html s))
    (.. parser (parseSlice el))))
